import { useEffect, useRef, useState } from "react";
import CustomButton from "../../../../components/CustomButton";
import ModalWrapper from "../../../../components/Modal/ModalWrapper";
import TableWidget from "../../../../components/tables/TableWidget";
import {
  checkEndpointStatus,
  endpointStatus,
  RejectedObject,
} from "../../../../helpers/apiHelper";
import {
  getRecallEntry,
  postGenerateRecallDocument,
  postRequestRecallAwb,
} from "../../../../redux/api/recall.api";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  RecallAwbInterface,
  RecallClientActionsDataInterface,
  RecallClientBatchSelectionInterface,
  RecallEntryDetailsDataInterface,
} from "../../../../redux/interfaces/recall.interface";
import {
  closeRecallClientBatch,
  setRecallClientBatchLoading,
} from "../../../../redux/reducer/recall.reducer";
import Text from "../../../../styleguide/Text";
import {
  canGenerateRecallDocument,
  canRequestRecallAwb,
  getRecallAwbResultDetail,
  getRecallAwbResultStatus,
} from "./recallClientActionsHelper";
import { getRecallClientsTableData } from "./tableConfigs/recallEntryDetailsTableConfig";
import {
  getRecallClientBatchTableData,
  RecallClientBatchResult,
  recallClientBatchTableHeader,
} from "./tableConfigs/recallClientBatchTableConfig";

function RecallClientBatchModal({
  selection,
}: {
  selection: RecallClientBatchSelectionInterface;
}) {
  const dispatch = useAppDispatch();
  const { clientBatch, pendingClientActionIds, awbAttemptedClientIds } =
    useAppSelector((state) => state.recall);
  const [results, setResults] = useState<RecallClientBatchResult[]>(
    selection.clients.map((client) => ({ ...client, status: "waiting" })),
  );
  const [isFinished, setIsFinished] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [message, setMessage] = useState("");
  const stopRequested = useRef(false);
  const isMounted = useRef(true);
  const isRunning = useRef(false);
  const isAwb = selection.action === "awb";
  const pharmacyCountLabel = `${selection.clients.length} ${
    selection.clients.length === 1 ? "farmacie" : "farmacii"
  }`;
  const isLoading = clientBatch.isLoading;
  const processedCount = results.filter(
    (item) => item.status !== "waiting" && item.status !== "processing",
  ).length;

  useEffect(() => {
    return () => {
      stopRequested.current = true;
      isMounted.current = false;
    };
  }, []);

  const updateResult = (
    clientPlId: number,
    status: RecallClientBatchResult["status"],
    detail?: string,
  ) => {
    if (!isMounted.current) return;
    setResults((previous) =>
      previous.map((item) =>
        item.clientPlId === clientPlId ? { ...item, status, detail } : item,
      ),
    );
  };

  const handleClose = () => {
    if (isRunning.current) {
      stopRequested.current = true;
      setIsStopping(true);
      return;
    }
    dispatch(closeRecallClientBatch());
  };

  const finish = async (refresh = true) => {
    if (!isMounted.current) return;
    if (refresh) await dispatch(getRecallEntry({ id: selection.idCampaign }));
    if (!isMounted.current) return;
    if (stopRequested.current) {
      setMessage(
        "Procesarea a fost oprita. Cererile deja trimise raman inregistrate.",
      );
    }
    isRunning.current = false;
    setIsFinished(true);
    dispatch(setRecallClientBatchLoading(false));
  };

  const handleStart = async () => {
    if (isRunning.current || isFinished || pendingClientActionIds.length)
      return;
    isRunning.current = true;
    dispatch(setRecallClientBatchLoading(true));

    // Verifica din nou grupul inainte de primele modificari.
    const entryAction = await dispatch(
      getRecallEntry({ id: selection.idCampaign }),
    );
    if (!isMounted.current) return;
    if (
      checkEndpointStatus(entryAction, getRecallEntry) !==
      endpointStatus.fulfilled
    ) {
      setMessage(
        "Datele retragerii nu au putut fi actualizate. Nu au fost trimise cereri pentru farmacii.",
      );
      await finish(false);
      return;
    }
    const clientsById = new Map(
      getRecallClientsTableData(
        entryAction.payload as RecallEntryDetailsDataInterface,
      ).map((row) => {
        const client: RecallClientActionsDataInterface = row.crudData;
        return [client.clientPlId, client] as const;
      }),
    );

    for (const selectedClient of selection.clients) {
      if (!isMounted.current || stopRequested.current) break;
      const client = clientsById.get(selectedClient.clientPlId);
      const isEligible =
        client &&
        (isAwb
          ? canRequestRecallAwb(client, awbAttemptedClientIds)
          : canGenerateRecallDocument(client));
      if (!client || !isEligible) {
        updateResult(
          selectedClient.clientPlId,
          "skipped",
          "Farmacia nu mai este eligibila pentru aceasta actiune",
        );
        continue;
      }
      updateResult(client.clientPlId, "processing");
      const params = {
        idCampaign: selection.idCampaign,
        clientPlId: client.clientPlId,
      };
      const resultAction = isAwb
        ? await dispatch(postRequestRecallAwb(params))
        : await dispatch(postGenerateRecallDocument(params));
      if (!isMounted.current) return;
      const status = isAwb
        ? checkEndpointStatus(resultAction, postRequestRecallAwb)
        : checkEndpointStatus(resultAction, postGenerateRecallDocument);

      if (status === endpointStatus.fulfilled) {
        if (isAwb) {
          const awb = resultAction.payload as RecallAwbInterface;
          const awbStatus = getRecallAwbResultStatus(awb);
          const awbDetail = getRecallAwbResultDetail(awb);
          if (awbStatus !== "sent") {
            updateResult(
              client.clientPlId,
              awbStatus,
              `${awbDetail}. Verifica situatia in TMS`,
            );
            setMessage(
              awbStatus === "pending"
                ? "Procesarea AWB-urilor s-a oprit la o solicitare in curs. Verifica situatia in TMS."
                : "Procesarea AWB-urilor s-a oprit la un rezultat neconfirmat. Cererea nu va fi retrimisa automat.",
            );
            break;
          }
          updateResult(client.clientPlId, "success", awbDetail);
        } else {
          updateResult(
            client.clientPlId,
            "success",
            "Document generat, disponibil pentru descarcare",
          );
        }
      } else {
        updateResult(
          client.clientPlId,
          "error",
          (resultAction.payload as RejectedObject)?.message ||
            "Cererea nu a putut fi finalizata",
        );
        if (isAwb) {
          setMessage(
            "Procesarea AWB-urilor s-a oprit dupa o eroare. Verifica rezultatul solicitarii inainte de alte actiuni.",
          );
          break;
        }
      }
    }
    await finish();
  };

  return (
    <ModalWrapper
      show
      modalSize="lg"
      title={`${isAwb ? "Solicita AWB" : "Genereaza documente"} - ${
        selection.title
      }`}
      handleClose={handleClose}>
      <div className="mt-4 space-y-3">
        <Text>
          {isAwb
            ? isLoading || isFinished
              ? `Solicitari AWB pentru ${pharmacyCountLabel} din acest grup.`
              : `Confirmi solicitarea in TMS a AWB-urilor pentru plic si retur pentru ${pharmacyCountLabel} din acest grup?`
            : `Documente de retragere pentru ${pharmacyCountLabel} din acest grup.`}
        </Text>
        {selection.excludedCount > 0 && (
          <Text>
            Farmacii excluse din aceasta actiune: {selection.excludedCount}
          </Text>
        )}
        <Text component="div" aria-live="polite">
          {isStopping && isLoading
            ? "Se opreste dupa cererea in curs"
            : isFinished
            ? "Procesare incheiata"
            : isLoading
            ? `Procesate: ${processedCount} / ${results.length}`
            : "Verifica farmaciile de mai jos inainte de confirmare"}
        </Text>
        {message && <Text role="alert">{message}</Text>}
      </div>
      <div className="mt-6">
        <TableWidget
          className="[&_thead_th]:text-left"
          tableHeaderData={recallClientBatchTableHeader}
          tableItemsData={getRecallClientBatchTableData(results, isFinished)}
          withTablePagination={false}
        />
      </div>
      <div className="flex flex-wrap justify-between gap-3">
        <CustomButton
          variant="outlined"
          disabled={isStopping && isLoading}
          onClick={handleClose}>
          {isLoading ? "Opreste" : isFinished ? "Inchide" : "Anuleaza"}
        </CustomButton>
        {!isFinished && (
          <CustomButton
            variant="contained"
            loading={isLoading}
            disabled={isLoading || pendingClientActionIds.length > 0}
            onClick={handleStart}>
            {isAwb ? "Trimite in TMS" : "Genereaza documente"}
          </CustomButton>
        )}
      </div>
    </ModalWrapper>
  );
}

export default RecallClientBatchModal;
